import { watch, type Ref } from 'vue';
import type { DataTableFilterMeta, DataTableFilterMetaData } from 'primevue/datatable';
import { FilterMatchMode } from '@primevue/core/api';

type FilterModel = { value: unknown };

export const numericFilterMatchModeOptions = [
  { label: 'Equals', value: FilterMatchMode.EQUALS },
  { label: 'Less Than', value: FilterMatchMode.LESS_THAN },
  { label: 'Greater Than', value: FilterMatchMode.GREATER_THAN },
  { label: 'Between', value: FilterMatchMode.BETWEEN },
];

export const dateFilterMatchModeOptions = [
  { label: 'Contains', value: FilterMatchMode.CONTAINS },
  { label: 'Before', value: FilterMatchMode.LESS_THAN },
  { label: 'After', value: FilterMatchMode.GREATER_THAN },
  { label: 'Between', value: FilterMatchMode.BETWEEN },
];

/**
 * Value/input helpers for the numeric and date row filters used by the CRM DataTables. Numeric
 * filters store numbers (or [min, max] for Between); date filters store Y-m-d strings (or a pair),
 * which compare correctly as strings against the Y-m-d values the grids receive.
 */
export const useColumnFilters = () => {
  const parseNumericFilterInput = (value: string | null): number | null => {
    if (value === null || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const numericFilterValue = (filterModel: FilterModel): string | null => {
    const { value } = filterModel;
    return Array.isArray(value) || value === null || value === undefined ? null : String(value);
  };

  const numericBetweenValue = (filterModel: FilterModel, index: 0 | 1): string | null => {
    const bounds = filterModel.value;
    const bound = Array.isArray(bounds) ? bounds[index] : null;
    return bound === null || bound === undefined ? null : String(bound);
  };

  const onNumericFilterInput = (filterModel: FilterModel, value: string | null, filterCallback: () => void) => {
    filterModel.value = parseNumericFilterInput(value);
    filterCallback();
  };

  const onNumericBetweenFilterInput = (
    filterModel: FilterModel,
    index: 0 | 1,
    value: string | null,
    filterCallback: () => void,
  ) => {
    const bounds = Array.isArray(filterModel.value) ? [...filterModel.value] : [null, null];
    bounds[index] = parseNumericFilterInput(value);
    filterModel.value = bounds;
    filterCallback();
  };

  const dateFilterValue = (filterModel: FilterModel): string | null =>
    (typeof filterModel.value === 'string' ? filterModel.value.slice(0, 10) : null);

  const dateBetweenValue = (filterModel: FilterModel, index: 0 | 1): string | null => {
    const bounds = filterModel.value;
    const bound = Array.isArray(bounds) ? bounds[index] : null;
    return typeof bound === 'string' ? bound.slice(0, 10) : null;
  };

  /**
   * @param upperBoundSuffix For `Y-m-d H:i` columns pass ' 23:59': it is appended to the After date and
   *   the Between upper bound so a datetime compares by day, the same as a plain Y-m-d column.
   */
  const onDateFilterInput = (
    filterModel: FilterModel & { matchMode?: string },
    value: string | null,
    filterCallback: () => void,
    upperBoundSuffix: string = '',
  ) => {
    const isUpperBound = filterModel.matchMode === FilterMatchMode.GREATER_THAN;
    filterModel.value = value ? `${value}${isUpperBound ? upperBoundSuffix : ''}` : null;
    filterCallback();
  };

  const onDateBetweenFilterInput = (
    filterModel: FilterModel,
    index: 0 | 1,
    value: string | null,
    filterCallback: () => void,
    upperBoundSuffix: string = '',
  ) => {
    const bounds = Array.isArray(filterModel.value) ? [...filterModel.value] : [null, null];
    bounds[index] = value ? `${value}${index === 1 ? upperBoundSuffix : ''}` : null;
    filterModel.value = bounds;
    filterCallback();
  };

  const asFilterMetaData = (entry?: DataTableFilterMeta[string]): DataTableFilterMetaData | undefined =>
    (entry && typeof entry === 'object' && 'matchMode' in entry ? entry : undefined);

  /**
   * Switching a numeric or date filter's operator (e.g. Between -> Equals) leaves the previous value
   * in place with a mismatched shape (array vs scalar), which would otherwise hide every row until
   * the user retypes a value. Reset the value whenever the operator changes.
   */
  const resetFilterValueOnMatchModeChange = (tableFilters: Ref<DataTableFilterMeta | undefined>, fields: readonly string[]) => {
    watch(
      () => fields.map(field => asFilterMetaData(tableFilters.value?.[field])?.matchMode),
      (current, previous) => {
        if (!previous) return;
        fields.forEach((field, index) => {
          // Skip when filters were hidden/restored (undefined) rather than the operator being switched.
          if (!current[index] || !previous[index] || current[index] === previous[index]) return;
          const entry = asFilterMetaData(tableFilters.value?.[field]);
          if (entry) entry.value = null;
        });
      },
    );
  };

  return {
    dateBetweenValue,
    dateFilterValue,
    numericBetweenValue,
    numericFilterValue,
    onDateBetweenFilterInput,
    onDateFilterInput,
    onNumericBetweenFilterInput,
    onNumericFilterInput,
    resetFilterValueOnMatchModeChange,
  };
};
